import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import Card from "../atoms/Card"
import CardBar from "../atoms/CardBar"
import H3 from "../atoms/typography/H3"
import H4 from "../atoms/typography/H4"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import SecondaryButton from "../atoms/buttons/SecondaryButton"
import { AddIcon } from "../atoms/icons/AddIcon"
import { CheckmarkIcon } from "../atoms/icons/CheckboxIcons"
import { CrossIcon } from "../atoms/icons/CrossIcon"
import AddressForm from "../molecules/AddressForm"
import TextInput from "../molecules/TextInput"
import { useCreateNewOrganization } from "../../hooks/useCreateNewOrganization"
import Subtitle from "../atoms/typography/Subtitle"
import { captureExceptionWithContext } from "../../utils/sentry"
import { ApolloError } from "@apollo/client"

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`

const billingInitialValue = {
  address1: undefined,
  address2: undefined,
  city: undefined,
  country: undefined,
  zipCode: undefined,
  vat: undefined,
}

const CreateSuborganizationOrganism: React.FunctionComponent = () => {
  const [open, setOpen] = React.useState(false)
  const [orgName, setOrgName] = React.useState("")
  const [billingAddress, setBillingAddress] = React.useState<any>(billingInitialValue)

  const [orgSuccessfullyCreated, setOrgSuccessfullyCreated] = React.useState(false)

  const handleChangeBillingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setBillingAddress((values: any) => ({ ...values, [name]: value }))
  }

  const [errorMessage, setErrorMessage] = React.useState<Array<string>>([])

  const checkFormFilled =
    billingAddress &&
    orgName &&
    billingAddress?.address1 &&
    billingAddress?.country &&
    billingAddress?.city &&
    billingAddress?.zipCode
      ? true
      : false

  const onCompletedNewOrg = (data: any) => {
    if (data?.createOrganization?.organization) {
      setOrgSuccessfullyCreated(true)
      setTimeout(() => setOpen(false), 2000)
      setTimeout(() => setOrgSuccessfullyCreated(false), 2000)
      setTimeout(() => setBillingAddress(billingInitialValue), 1000)
      setErrorMessage([])
    }
  }
  const onErrorNewOrg = (error: ApolloError) => {
    captureExceptionWithContext(error, "error creating new org")
  }
  const { createNewOrganization, data } = useCreateNewOrganization({
    onCompleted: (data: any) => onCompletedNewOrg(data),
    onError: onErrorNewOrg,
  })
  const onCreateNewOrg = async () => {
    setErrorMessage([])
    if (checkFormFilled) {
      try {
        await createNewOrganization({
          variables: {
            input: {
              name: orgName,
              address1: billingAddress?.address1,
              address2: billingAddress?.address2,
              country: billingAddress?.country ?? "AU",
              city: billingAddress?.city,
              zipCode: billingAddress?.zipCode,
              vat: billingAddress?.vat,
              isSubOrg: true,
            },
          },
        })
      } catch (error) {
        captureExceptionWithContext(error, "error creating new org")
      }
    } else {
      if (!orgName) {
        setErrorMessage([...errorMessage, "Fill in organization name"])
      }
      if (!billingAddress?.address1) {
        setErrorMessage([...errorMessage, "Fill in address"])
      }
      if (!billingAddress?.country) {
        setErrorMessage([...errorMessage, "Fill in country"])
      }
      if (!billingAddress?.city) {
        setErrorMessage([...errorMessage, "Fill in city"])
      }
      if (!billingAddress?.zipCode) {
        setErrorMessage([...errorMessage, "Fill in zip code"])
      }
    }
  }

  const onClickCreateNewSuborg = () => {
    onCreateNewOrg()
  }

  return (
    <div>
      <Card noPadding>
        <CardBar open={open} onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          <Row justify="center" align="center">
            <AddIcon color={theme.colors.white} />
            <Spacer spacing={1} />
            <Subtitle color={theme.colors.white}>Create new suborganization</Subtitle>
          </Row>
        </CardBar>
        <SlideOutHeight isVisible={open}>
          <ContentContainer>
            <HorizontalSpacer spacing={2} />
            <Row justify="center">
              <TextInput placeholder="Suborganization name" onChange={(e) => setOrgName(e.target.value)} required />
            </Row>
            <HorizontalSpacer spacing={4} />
            <Row justify="between">
              <Col md={5}>
                <H3>Billing Address</H3>
                <HorizontalSpacer spacing={1} />
                <AddressForm onChange={handleChangeBillingAddress} requiredFields />
              </Col>
            </Row>
            <HorizontalSpacer spacing={4} />

            {data?.createOrganization?.errors?.length > 0 && (
              <div>
                <HorizontalSpacer spacing={1} />
                <H3 color={theme.colors.red}>{data?.createOrganization?.errors[0].messages}</H3>
                <HorizontalSpacer spacing={1} />
              </div>
            )}

            {errorMessage && (
              <div>
                <HorizontalSpacer spacing={1} />
                {errorMessage.map((em, index) => (
                  <H3 key={index} color={theme.colors.red}>
                    {em}
                  </H3>
                ))}

                <HorizontalSpacer spacing={1} />
              </div>
            )}

            {orgSuccessfullyCreated && data?.createOrganization?.errors?.length < 1 && (
              <Row justify="center">
                <H4 color={theme.colors.greenDark}>Suborganization successfully created</H4>
                <HorizontalSpacer spacing={2} />
              </Row>
            )}

            <Row justify="center">
              <SecondaryButton
                title="Create suborganization"
                icon={<CheckmarkIcon color={theme.colors.white} />}
                onClick={onClickCreateNewSuborg}
              />
              <Spacer spacing={1} />
              <PrimaryButton title="Cancel" icon={<CrossIcon />} onClick={() => setOpen(!open)} />
            </Row>
          </ContentContainer>
        </SlideOutHeight>
      </Card>
    </div>
  )
}

export default CreateSuborganizationOrganism
